import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Dumbbell, Footprints, Users, DollarSign, Trophy, ArrowRight, Check, X, Zap, Clock, Target } from 'lucide-react';
import { setProgressForSlug } from '../../pages/GuidesHubPage';
import RelatedGuides from './RelatedGuides';

const ARTICLE_SLUG = 'hyrox-vs-crossfit-comparison';

const NAV = [
  { id: 'format', label: 'Format' },
  { id: 'compare', label: 'Compare' },
  { id: 'cost', label: 'Cost' },
  { id: 'quiz', label: 'Quiz' },
  { id: 'verdict', label: 'Verdict' },
];

const COMPARE_CATEGORIES = [
  {
    label: 'Training Style',
    hyrox: { text: 'Predictable stations, heavy running focus, endurance-dominant', rating: 4 },
    crossfit: { text: 'Varied WODs, Olympic lifts, gymnastics, constantly changing', rating: 5 },
  },
  {
    label: 'Community',
    hyrox: { text: 'Event-focused gatherings, race-day atmosphere', rating: 3 },
    crossfit: { text: 'Daily class community, strong social bonds, local comps', rating: 5 },
  },
  {
    label: 'Skill Variety',
    hyrox: { text: '8 fixed stations + running — master once, repeat', rating: 2 },
    crossfit: { text: 'Olympic lifting, gymnastics, rowing, swimming, running', rating: 5 },
  },
  {
    label: 'Measurable Progress',
    hyrox: { text: 'Global ranking system, standardized format, clear PBs', rating: 5 },
    crossfit: { text: 'Varied benchmarks, Open percentiles, broad fitness', rating: 4 },
  },
  {
    label: 'Accessibility',
    hyrox: { text: 'Train anywhere — gym, home, outdoors. Minimal equipment', rating: 5 },
    crossfit: { text: 'Requires affiliate gym with specialized equipment', rating: 3 },
  },
  {
    label: 'Competition',
    hyrox: { text: 'Race events worldwide, Doubles & Relay categories', rating: 4 },
    crossfit: { text: 'Open → Quarterfinals → Semis → Games pipeline', rating: 5 },
  },
];

const QUIZ_QUESTIONS = [
  { q: 'Do you prefer racing or daily workouts?', a: 'Racing events', b: 'Daily variety' },
  { q: 'Is community or independence more important?', a: 'Independence', b: 'Daily community' },
  { q: 'What\'s your running background?', a: 'Strong runner', b: 'Not a runner' },
  { q: 'Do you want to learn Olympic lifts?', a: 'No, keep it simple', b: 'Yes, teach me everything' },
  { q: 'What\'s your budget preference?', a: 'Lower cost (gym optional)', b: 'Higher — coaching included' },
];

const FORMAT_HYROX = [
  { label: '8x 1km Runs', icon: Footprints, detail: 'Between every station' },
  { label: '8 Stations', icon: Target, detail: 'Ski Erg, Sled Push/Pull, Burpees, Rowing, Farmers, Lunges, Wall Balls' },
  { label: '60-90 min', icon: Clock, detail: 'Total race duration' },
  { label: 'Global Ranking', icon: Trophy, detail: 'Compare your time worldwide' },
];

const FORMAT_CF = [
  { label: 'Daily WODs', icon: Zap, detail: 'Constantly varied programming' },
  { label: 'Olympic Lifts', icon: Dumbbell, detail: 'Snatch, Clean & Jerk, Squats' },
  { label: 'Gymnastics', icon: Target, detail: 'Pull-ups, Muscle-ups, HSPUs' },
  { label: 'Community', icon: Users, detail: 'Class-based with coaching' },
];

export default function HyroxVsCrossfitArticle() {
  const [activeNav, setActiveNav] = useState('format');
  const [activeCompare, setActiveCompare] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(100, Math.round((scrollTop / docHeight) * 100)) : 0;
      setProgressForSlug(ARTICLE_SLUG, pct);
      const sections = NAV.map(n => document.getElementById(n.id));
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].getBoundingClientRect().top <= 150) { setActiveNav(NAV[i].id); break; }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const hyroxScore = Object.values(quizAnswers).filter(v => v === 'a').length;
  const cfScore = Object.values(quizAnswers).filter(v => v === 'b').length;
  const quizComplete = Object.keys(quizAnswers).length === QUIZ_QUESTIONS.length;

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0a0a0a] text-zinc-300">
      <div className="sticky top-[60px] z-40 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto py-2 scrollbar-hide">
          {NAV.map(n => (
            <button key={n.id} onClick={() => document.getElementById(n.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className={`px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap transition-all ${activeNav === n.id ? 'bg-[#c41e3a] text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`nav-${n.id}`}>{n.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8 pb-20">
        {/* Hero */}
        <div className="mb-12">
          <span className="text-[#c41e3a] text-[10px] uppercase tracking-widest font-heading">HYROX / Comparison</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mt-3 mb-4 leading-tight">HYROX vs CrossFit</h1>
          <p className="text-zinc-400 text-base max-w-xl">Two paths to fitness excellence. Interactive comparison, cost breakdown, and a 5-question quiz to find your match.</p>
          <div className="flex items-center gap-4 mt-4 text-xs text-zinc-600 font-mono"><span>11 min read</span><span>Interactive quiz</span></div>
        </div>

        {/* Section: Format */}
        <section id="format" className="mb-16 scroll-mt-28" data-testid="section-format">
          <h2 className="font-heading text-2xl text-white mb-6">The Format Face-Off</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[#c41e3a] text-[10px] font-heading uppercase tracking-widest mb-3">HYROX</p>
              <div className="space-y-2">
                {FORMAT_HYROX.map(f => { const Icon = f.icon; return (
                  <div key={f.label} className="flex items-center gap-3 bg-[#c41e3a]/5 border border-[#c41e3a]/15 p-3">
                    <Icon className="w-4 h-4 text-[#c41e3a] flex-shrink-0" />
                    <div><p className="text-white text-sm font-bold">{f.label}</p><p className="text-zinc-500 text-xs">{f.detail}</p></div>
                  </div>
                ); })}
              </div>
            </div>
            <div>
              <p className="text-[#3b82f6] text-[10px] font-heading uppercase tracking-widest mb-3">CrossFit</p>
              <div className="space-y-2">
                {FORMAT_CF.map(f => { const Icon = f.icon; return (
                  <div key={f.label} className="flex items-center gap-3 bg-[#3b82f6]/5 border border-[#3b82f6]/15 p-3">
                    <Icon className="w-4 h-4 text-[#3b82f6] flex-shrink-0" />
                    <div><p className="text-white text-sm font-bold">{f.label}</p><p className="text-zinc-500 text-xs">{f.detail}</p></div>
                  </div>
                ); })}
              </div>
            </div>
          </div>
        </section>

        {/* Section: Compare */}
        <section id="compare" className="mb-16 scroll-mt-28" data-testid="section-compare">
          <h2 className="font-heading text-2xl text-white mb-2">Head-to-Head Comparison</h2>
          <p className="text-zinc-500 text-sm mb-6">Click each category to see the full breakdown.</p>
          <div className="space-y-2">
            {COMPARE_CATEGORIES.map((c, i) => {
              const isOpen = activeCompare === i;
              return (
                <div key={c.label} className={`border transition-all cursor-pointer ${isOpen ? 'border-zinc-700 bg-zinc-900/60' : 'border-zinc-800/50 hover:border-zinc-700'}`}
                  onClick={() => setActiveCompare(isOpen ? -1 : i)} data-testid={`compare-${i}`}>
                  <div className="flex items-center justify-between p-4">
                    <span className="text-white text-sm font-bold">{c.label}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="bg-[#c41e3a]/5 border border-[#c41e3a]/15 p-3">
                        <p className="text-[#c41e3a] text-[10px] font-heading uppercase tracking-widest mb-1">HYROX</p>
                        <p className="text-zinc-400 text-xs leading-relaxed mb-2">{c.hyrox.text}</p>
                        <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <div key={n} className={`h-1.5 flex-1 ${n <= c.hyrox.rating ? 'bg-[#c41e3a]' : 'bg-zinc-800'}`} />)}</div>
                      </div>
                      <div className="bg-[#3b82f6]/5 border border-[#3b82f6]/15 p-3">
                        <p className="text-[#3b82f6] text-[10px] font-heading uppercase tracking-widest mb-1">CrossFit</p>
                        <p className="text-zinc-400 text-xs leading-relaxed mb-2">{c.crossfit.text}</p>
                        <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <div key={n} className={`h-1.5 flex-1 ${n <= c.crossfit.rating ? 'bg-[#3b82f6]' : 'bg-zinc-800'}`} />)}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Section: Cost */}
        <section id="cost" className="mb-16 scroll-mt-28" data-testid="section-cost">
          <h2 className="font-heading text-2xl text-white mb-6">Annual Cost Comparison</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-zinc-900/60 border border-[#c41e3a]/20 p-5">
              <p className="text-[#c41e3a] text-[10px] font-heading uppercase tracking-widest mb-3">HYROX Annual Cost</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Race entries (3-4/year)</span><span className="text-white font-mono">$300-600</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Gym membership (optional)</span><span className="text-white font-mono">$0-600</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Travel to events</span><span className="text-white font-mono">$200-800</span></div>
                <div className="flex justify-between border-t border-zinc-800 pt-2"><span className="text-white font-bold">Total Range</span><span className="text-[#c41e3a] font-heading font-bold">$500-2,000</span></div>
              </div>
            </div>
            <div className="bg-zinc-900/60 border border-[#3b82f6]/20 p-5">
              <p className="text-[#3b82f6] text-[10px] font-heading uppercase tracking-widest mb-3">CrossFit Annual Cost</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Monthly membership</span><span className="text-white font-mono">$1,800-3,000</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Open registration</span><span className="text-white font-mono">$20</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Local competitions</span><span className="text-white font-mono">$100-400</span></div>
                <div className="flex justify-between border-t border-zinc-800 pt-2"><span className="text-white font-bold">Total Range</span><span className="text-[#3b82f6] font-heading font-bold">$1,920-3,420</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Quiz */}
        <section id="quiz" className="mb-16 scroll-mt-28" data-testid="section-quiz">
          <h2 className="font-heading text-2xl text-white mb-2">Which Is Right for You?</h2>
          <p className="text-zinc-500 text-sm mb-6">Answer 5 questions to find your match.</p>
          <div className="space-y-3">
            {QUIZ_QUESTIONS.map((q, i) => (
              <div key={q.q} className="bg-zinc-900/40 border border-zinc-800/50 p-4" data-testid={`quiz-q-${i}`}>
                <p className="text-white text-sm font-bold mb-3">{i + 1}. {q.q}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => { setQuizAnswers(p => ({...p, [i]: 'a'})); setShowResult(false); }}
                    className={`py-2.5 px-3 text-xs font-heading uppercase tracking-wider border transition-all text-left ${quizAnswers[i] === 'a' ? 'border-[#c41e3a] bg-[#c41e3a]/10 text-[#c41e3a]' : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}
                    data-testid={`quiz-${i}-a`}>
                    <span className="text-[10px] text-zinc-600 mr-2">A.</span> {q.a}
                  </button>
                  <button onClick={() => { setQuizAnswers(p => ({...p, [i]: 'b'})); setShowResult(false); }}
                    className={`py-2.5 px-3 text-xs font-heading uppercase tracking-wider border transition-all text-left ${quizAnswers[i] === 'b' ? 'border-[#3b82f6] bg-[#3b82f6]/10 text-[#3b82f6]' : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}
                    data-testid={`quiz-${i}-b`}>
                    <span className="text-[10px] text-zinc-600 mr-2">B.</span> {q.b}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {quizComplete && !showResult && (
            <button onClick={() => setShowResult(true)}
              className="mt-4 w-full py-3 bg-white text-black text-xs font-heading uppercase tracking-wider hover:bg-zinc-200 transition-colors"
              data-testid="quiz-submit">See My Result</button>
          )}

          {showResult && (
            <div className="mt-4 p-6 border" style={{ borderColor: hyroxScore >= cfScore ? '#c41e3a' : '#3b82f6', backgroundColor: hyroxScore >= cfScore ? 'rgba(196,30,58,0.05)' : 'rgba(59,130,246,0.05)' }} data-testid="quiz-result">
              <p className="text-white font-heading text-xl mb-2">
                {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                {hyroxScore > cfScore ? 'You\'re a HYROX Athlete' : hyroxScore < cfScore ? 'You\'re a CrossFit Athlete' : 'You\'re a Hybrid Athlete — Do Both!'}
              </p>
              <div className="flex items-center gap-4 mb-3">
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#c41e3a]" /><span className="text-xs text-zinc-400">HYROX: {hyroxScore}/5</span></div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#3b82f6]" /><span className="text-xs text-zinc-400">CrossFit: {cfScore}/5</span></div>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">
                {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                {hyroxScore > cfScore
                  ? 'You love clear goals, independent training, and race-day competition. HYROX gives you a global ranking to chase and a standardized format to master.'
                  : hyroxScore < cfScore
                  ? 'You thrive with daily community, varied challenges, and coaching. CrossFit\'s constantly varied programming will keep you engaged and accountable.'
                  : 'You value both structure and variety. Train CrossFit year-round for your base, then phase in HYROX-specific prep 8-12 weeks before your target race.'}
              </p>
            </div>
          )}
        </section>

        {/* Section: Verdict */}
        <section id="verdict" className="mb-16 scroll-mt-28" data-testid="section-verdict">
          <h2 className="font-heading text-2xl text-white mb-4">The Verdict</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-[#c41e3a]/5 border border-[#c41e3a]/20 p-4">
              <p className="text-[#c41e3a] text-xs font-heading uppercase tracking-wider mb-2">Choose HYROX If</p>
              <ul className="text-zinc-400 text-xs space-y-1">
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#c41e3a] mt-0.5 flex-shrink-0" /> You love running</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#c41e3a] mt-0.5 flex-shrink-0" /> Want clear, measurable goals</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#c41e3a] mt-0.5 flex-shrink-0" /> Prefer independent training</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#c41e3a] mt-0.5 flex-shrink-0" /> Thrive on race-day pressure</li>
              </ul>
            </div>
            <div className="bg-[#3b82f6]/5 border border-[#3b82f6]/20 p-4">
              <p className="text-[#3b82f6] text-xs font-heading uppercase tracking-wider mb-2">Choose CrossFit If</p>
              <ul className="text-zinc-400 text-xs space-y-1">
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#3b82f6] mt-0.5 flex-shrink-0" /> Want daily community</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#3b82f6] mt-0.5 flex-shrink-0" /> Enjoy varied challenges</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#3b82f6] mt-0.5 flex-shrink-0" /> Value coaching & classes</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#3b82f6] mt-0.5 flex-shrink-0" /> Want strength + cardio</li>
              </ul>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-700/50 p-4">
              <p className="text-zinc-300 text-xs font-heading uppercase tracking-wider mb-2">Choose Both If</p>
              <ul className="text-zinc-400 text-xs space-y-1">
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-zinc-400 mt-0.5 flex-shrink-0" /> Want to be well-rounded</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-zinc-400 mt-0.5 flex-shrink-0" /> Have time and budget</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-zinc-400 mt-0.5 flex-shrink-0" /> CF base + HYROX peaks</li>
                <li className="flex items-start gap-1.5"><Check className="w-3 h-3 text-zinc-400 mt-0.5 flex-shrink-0" /> Love competition of any kind</li>
              </ul>
            </div>
          </div>
          <p className="text-zinc-500 text-sm mt-6 leading-relaxed">Neither is "better" — they're different tools for different goals. The best choice is the one you'll actually do consistently.</p>
        </section>

        <RelatedGuides currentSlug={ARTICLE_SLUG} />

        <div className="bg-amber-950/20 border border-amber-700/30 p-4 text-xs text-amber-200/70 leading-relaxed">
          <strong className="text-amber-200">Note:</strong> This comparison is for informational purposes. Both HYROX and CrossFit are excellent fitness systems. Consult a coach or physician before starting any new training program.
        </div>
      </div>
    </div>
  );
}
